/**
 * Gerente de comandos, equivalente a `TCommandManager` / `TCharPrefixCommandManager`.
 * Comportamento em docs/spec/chat-commands.md.
 */
import { decodeCommandRequest, foldTrigger, normalizeTriggers, type CommandRequest } from './request.js';

export type CommandResponse =
  | { readonly kind: 'ok' }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'notExecuted'; readonly message: string | null }
  | { readonly kind: 'notFound'; readonly commandId: string };

export const ok = (): CommandResponse => ({ kind: 'ok' });
export const error = (message: string): CommandResponse => ({ kind: 'error', message });
export const notExecuted = (message: string | null = null): CommandResponse => ({ kind: 'notExecuted', message });

/** Título e linhas de descrição de um comando (`TCommandManual`). */
export type CommandManual = {
  readonly title: string | null;
  readonly lines: readonly string[];
};

export type CommandEntry<Env> = {
  /** o primeiro gatilho é o nome canônico */
  readonly triggers: readonly string[];
  /** função, e não valor, para que o texto acompanhe a troca de idioma */
  readonly manual?: () => CommandManual;
  readonly execute: (request: CommandRequest, env: Env) => CommandResponse | void;
};

/** Textos genéricos do gerente (`commandMgr.*`), injetados para seguir o idioma. */
export type CommandManagerMessages = {
  unknownCommand: (commandId: string, helpTrigger: string | null) => string;
  errorWithoutMessage: (commandId: string) => string;
};

const DEFAULT_MESSAGES: CommandManagerMessages = {
  unknownCommand: (commandId, helpTrigger) =>
    helpTrigger
      ? `Comando desconhecido: ${commandId}. Use ${helpTrigger} para ver os comandos.`
      : `Comando desconhecido: ${commandId}`,
  errorWithoutMessage: (commandId) => `O comando "${commandId}" falhou sem dizer por quê.`,
};

export type CommandManagerOptions = {
  /** caractere printável que abre a linha de comando; `null` = tudo é comando (console) */
  readonly prefix?: string | null;
  /** gatilho do comando de ajuda, citado quando o comando não existe */
  readonly helpTrigger?: string | null;
  readonly messages?: Partial<CommandManagerMessages>;
};

/** `TCharPrefixCommandManager.TestIsValidCharPrefix`: um code point printável. */
export function isValidCharPrefix(prefix: string): boolean {
  if (Array.from(prefix).length !== 1) return false;
  return !/^[\p{Cc}\p{Cf}\p{Cs}\p{Co}\p{Cn}\s]$/u.test(prefix);
}

export class CommandManager<Env> {
  readonly prefix: string | null;
  readonly helpTrigger: string | null;
  private readonly messages: CommandManagerMessages;
  private readonly byTrigger = new Map<string, CommandEntry<Env>>();
  private readonly entries: CommandEntry<Env>[] = [];

  constructor(options: CommandManagerOptions = {}) {
    const prefix = options.prefix ?? null;
    if (prefix !== null && !isValidCharPrefix(prefix)) {
      throw new Error(`prefixo de comando inválido: ${JSON.stringify(prefix)}`);
    }
    this.prefix = prefix;
    this.helpTrigger = options.helpTrigger ?? null;
    this.messages = { ...DEFAULT_MESSAGES, ...options.messages };
  }

  /** quanto pular no começo da linha antes de ler o identificador */
  get skipCount(): number {
    return this.prefix === null ? 0 : this.prefix.length;
  }

  register(entry: CommandEntry<Env>): this {
    const triggers = normalizeTriggers(entry.triggers);
    if (triggers.length === 0) throw new Error('comando sem gatilho');
    const normalized: CommandEntry<Env> = { ...entry, triggers };
    this.entries.push(normalized);
    for (const trigger of triggers) this.byTrigger.set(foldTrigger(trigger), normalized);
    return this;
  }

  /** `TestCanHandleAsCommandLine`: a linha começa com o prefixo? */
  canHandle(line: string): boolean {
    if (this.prefix === null) return line.trim().length > 0;
    return line.startsWith(this.prefix);
  }

  decode(line: string): CommandRequest | null {
    if (!this.canHandle(line)) return null;
    return decodeCommandRequest(line, this.skipCount);
  }

  find(commandId: string): CommandEntry<Env> | undefined {
    return this.byTrigger.get(foldTrigger(commandId));
  }

  /** `GetContainsRegisteredCommand`: a linha é um comando que existe? */
  containsRegisteredCommand(line: string): boolean {
    const request = this.decode(line);
    return request !== null && this.find(request.commandId) !== undefined;
  }

  execute(line: string, env: Env): CommandResponse {
    const request = this.decode(line);
    if (!request) return { kind: 'notFound', commandId: '' };
    const entry = this.find(request.commandId);
    if (!entry) return { kind: 'notFound', commandId: request.commandId };
    const response = entry.execute(request, env) ?? ok();
    if (response.kind === 'error' && response.message.trim().length === 0) {
      return error(this.messages.errorWithoutMessage(entry.triggers[0]!));
    }
    return response;
  }

  /** Mensagem pronta para o log, ou `null` quando deu tudo certo em silêncio. */
  describe(response: CommandResponse): string | null {
    switch (response.kind) {
      case 'ok':
        return null;
      case 'error':
        return response.message;
      case 'notExecuted':
        return response.message;
      case 'notFound':
        return this.messages.unknownCommand(this.format(response.commandId), this.helpLabel());
    }
  }

  /** Manuais de todos os comandos que têm um, em ordem do nome canônico. */
  manuals(): { command: string; manual: CommandManual }[] {
    return this.entries
      .filter((entry) => entry.manual !== undefined)
      .map((entry) => ({ command: entry.triggers[0]!, manual: entry.manual!() }))
      .sort((a, b) => a.command.localeCompare(b.command, 'pt-BR'));
  }

  /** `/me` a partir de `me` — o jeito de citar um comando. */
  format(commandId: string): string {
    return `${this.prefix ?? ''}${commandId}`;
  }

  private helpLabel(): string | null {
    return this.helpTrigger === null ? null : this.format(this.helpTrigger);
  }
}

/**
 * Comando dobrado: o primeiro argumento é a ação (`/plugins instalar …`). Sem ação, erro
 * pedindo a ajuda; com a ação de ajuda, lista as ações.
 */
export type FoldedAction<Env> = {
  readonly triggers: readonly string[];
  readonly manual?: () => CommandManual;
  readonly execute: (request: CommandRequest, env: Env) => CommandResponse | void;
};

export type FoldedCommandOptions<Env> = {
  readonly triggers: readonly string[];
  readonly actions: readonly FoldedAction<Env>[];
  readonly manual?: () => CommandManual;
  /** gatilhos que listam as ações; o primeiro aparece nas mensagens de erro */
  readonly helpTriggers?: readonly string[];
  readonly messages: {
    missingAction: (command: string, helpAction: string) => string;
    unknownAction: (command: string, action: string, helpAction: string) => string;
    availableActions: (command: string, actions: readonly string[]) => string;
  };
};

export function foldedCommand<Env>(options: FoldedCommandOptions<Env>): CommandEntry<Env> {
  const helpTriggers = normalizeTriggers(options.helpTriggers ?? ['ajuda', 'help']);
  const actions = options.actions.map((action) => ({ ...action, triggers: normalizeTriggers(action.triggers) }));
  const byTrigger = new Map<string, (typeof actions)[number]>();
  for (const action of actions) {
    for (const trigger of action.triggers) byTrigger.set(foldTrigger(trigger), action);
  }
  const command = normalizeTriggers(options.triggers)[0]!;
  const helpAction = helpTriggers[0]!;
  const names = actions.map((action) => action.triggers[0]!);

  return {
    triggers: options.triggers,
    ...(options.manual ? { manual: options.manual } : {}),
    execute(request, env) {
      const inner = decodeCommandRequest(request.args, 0);
      if (!inner) return error(options.messages.missingAction(command, helpAction));
      if (helpTriggers.some((trigger) => foldTrigger(trigger) === foldTrigger(inner.commandId))) {
        return notExecuted(options.messages.availableActions(command, names));
      }
      const action = byTrigger.get(foldTrigger(inner.commandId));
      if (!action) return error(options.messages.unknownAction(command, inner.commandId, helpAction));
      return action.execute(inner, env) ?? ok();
    },
  };
}
